import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuth } from '../services/auth';
import { useToast } from '../context/ToastContext';
import { AlertCircle, CheckCircle2, Clock, Image as ImageIcon, LifeBuoy, MessageSquare, Send, Upload, X, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const SupportPage = () => {
    const user = getAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState<any[]>([]);
    const [ticketType, setTicketType] = useState('FALHA');
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [closeModal, setCloseModal] = useState<{ isOpen: boolean; ticketId: string | null; loading: boolean }>({
        isOpen: false,
        ticketId: null,
        loading: false
    });

    useEffect(() => {
        if (user?.tenant_id) {
            fetchTickets();
        }
    }, [user?.tenant_id]);

    const fetchTickets = async () => {
        try {
            const res = await axios.get(`https://zapshop.app/api/support/tickets/${user?.tenant_id}`);
            setTickets(res.data);
        } catch (err) {
            console.error('Erro ao buscar chamados:', err);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setScreenshot(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!description) return;

        setLoading(true);
        const formData = new FormData();
        formData.append('tenant_id', user?.tenant_id || '');
        formData.append('ticket_type', ticketType);
        formData.append('description', description);
        if (screenshot) {
            formData.append('screenshot', screenshot);
        }

        try {
            await axios.post('https://zapshop.app/api/support', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showToast('Chamado aberto com sucesso!', 'success');
            setDescription('');
            setScreenshot(null);
            setPreviewUrl(null);
            fetchTickets();
        } catch (err) {
            showToast('Erro ao abrir chamado.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTicket = (id: string) => {
        setCloseModal({ isOpen: true, ticketId: id, loading: false });
    };

    const confirmCloseTicket = async () => {
        if (!closeModal.ticketId) return;
        setCloseModal(prev => ({ ...prev, loading: true }));
        try {
            await axios.put(`https://zapshop.app/api/support/tickets/${closeModal.ticketId}/status`, {
                status: 'CLOSED',
                tenant_id: user?.tenant_id
            });
            showToast('Chamado finalizado com sucesso!', 'success');
            setCloseModal({ isOpen: false, ticketId: null, loading: false });
            fetchTickets();
        } catch (err) {
            showToast('Erro ao finalizar chamado.', 'error');
            setCloseModal(prev => ({ ...prev, loading: false }));
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'OPEN':
                return <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold">ABERTO</span>;
            case 'REPLIED':
                return <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-bold">RESPONDIDO</span>;
            case 'CLOSED':
                return <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-[10px] font-bold">FINALIZADO</span>;
            default:
                return null;
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-fade-in px-4 py-6 md:px-0">
            {/* Header Section */}
            <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <MessageSquare className="text-emerald-600" /> Suporte ao Lojista
                </h2>
                <p className="text-gray-500 text-sm mt-1">Precisa de ajuda ou tem alguma sugestão?</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="flex flex-col gap-8">
                    {/* Form Section - Wide */}
                    <div className="w-full">
                        <div className="bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="font-bold text-gray-900 text-base mb-6">Abrir Novo Chamado</h3>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Motivo do Contato</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setTicketType('FALHA')}
                                                className={`py-2.5 px-3 rounded-xl border text-xs font-medium transition-all ${ticketType === 'FALHA'
                                                    ? 'bg-red-50 border-red-200 text-red-700'
                                                    : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200'
                                                    }`}
                                            >
                                                Pane/Falha
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setTicketType('MELHORIA')}
                                                className={`py-2.5 px-3 rounded-xl border text-xs font-medium transition-all ${ticketType === 'MELHORIA'
                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                    : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200'
                                                    }`}
                                            >
                                                Melhoria
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Anexar Print (Opcional)</label>
                                        {!previewUrl ? (
                                            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-100 rounded-xl p-6 cursor-pointer hover:border-emerald-200 hover:bg-emerald-50/30 transition-all">
                                                <Upload className="text-gray-300 mb-2" size={24} />
                                                <span className="text-[10px] font-medium text-gray-500">Clique para enviar imagem</span>
                                                <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                            </label>
                                        ) : (
                                            <div className="relative rounded-xl overflow-hidden group">
                                                <img src={previewUrl} alt="Preview" className="w-full h-32 object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => { setScreenshot(null); setPreviewUrl(null); }}
                                                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg transition-opacity"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Descrição Detalhada</label>
                                        <textarea
                                            required
                                            rows={5}
                                            className="input-primary w-full text-sm py-4"
                                            placeholder="Explique o que ocorreu ou sua sugestão..."
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </div>

                                    <button
                                        disabled={loading}
                                        className="w-full btn-primary py-4 flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/10"
                                    >
                                        {loading ? 'Enviando...' : (
                                            <>
                                                <Send size={18} />
                                                Enviar Chamado
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>

                            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="flex gap-2 text-gray-500 text-left">
                                    <Clock size={16} className="shrink-0" />
                                    <p className="text-[10px] leading-relaxed">
                                        <b>Aviso:</b> Os chamados serão atendidos num prazo máximo de <b>48h úteis</b>. Acompanhe o status nesta página.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* History Section */}
                    <div className="w-full space-y-4">
                        <h3 className="font-black text-gray-400 uppercase text-[10px] tracking-[0.2em] px-2 md:px-0">Meus Chamados</h3>

                        {tickets.length === 0 ? (
                            <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-200 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                    <LifeBuoy size={32} className="text-gray-300" />
                                </div>
                                <p className="text-gray-500 font-medium text-sm md:text-base">Você ainda não abriu nenhum chamado.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {tickets.map((ticket) => (
                                    <div key={ticket.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
                                        <div className="p-4 md:p-6">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex items-center gap-2">
                                                    {getStatusBadge(ticket.status)}
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">#{ticket.id.slice(0, 8)}</span>
                                                </div>
                                                <span className="text-[10px] text-gray-400 uppercase font-bold">{new Date(ticket.created_at).toLocaleDateString()}</span>
                                            </div>

                                            <div className="flex flex-col md:flex-row gap-4">
                                                <div className="flex-1 order-2 md:order-1 text-left">
                                                    <div className="flex items-center gap-1.5 mb-2">
                                                        {ticket.ticket_type === 'FALHA' ? (
                                                            <AlertCircle size={14} className="text-red-500" />
                                                        ) : (
                                                            <CheckCircle2 size={14} className="text-emerald-500" />
                                                        )}
                                                        <span className="text-xs font-bold text-gray-700">{ticket.ticket_type}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 leading-relaxed font-medium">{ticket.description}</p>
                                                </div>
                                                <div className="flex flex-col gap-2 shrink-0 order-1 md:order-2">
                                                    {ticket.image_url && (
                                                        <a href={`https://zapshop.app${ticket.image_url}`} target="_blank" rel="noreferrer" className="w-full md:w-32 h-40 md:h-24 rounded-xl overflow-hidden border border-gray-100 hover:opacity-80 transition-opacity">
                                                            <img src={`https://zapshop.app${ticket.image_url}`} alt="Anexo" className="w-full h-full object-cover" />
                                                        </a>
                                                    )}
                                                    {ticket.status !== 'CLOSED' && (
                                                        <button
                                                            onClick={() => handleCloseTicket(ticket.id)}
                                                            className="bg-red-50 text-red-600 border border-red-100 font-black hover:bg-red-600 hover:text-white transition-all rounded-xl px-4 py-2.5 text-[10px] uppercase tracking-widest shadow-sm active:scale-95"
                                                        >
                                                            Finalizar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {ticket.admin_reply && (
                                            <div className="bg-emerald-50/50 p-4 md:p-6 border-t border-emerald-100 flex gap-4 text-left">
                                                <div className="w-10 h-10 rounded-full bg-emerald-100 md:flex items-center justify-center shrink-0 hidden">
                                                    <MessageSquare size={18} className="text-emerald-700" />
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                        <MessageSquare size={12} className="md:hidden" />
                                                        Resposta do Suporte:
                                                    </p>
                                                    <p className="text-sm text-emerald-900 leading-relaxed font-bold">{ticket.admin_reply}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="space-y-6">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] px-1">Perguntas Frequentes (FAQ)</h3>
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
                        <FAQItem
                            question="Como conecto meu WhatsApp?"
                            answer="Vá na aba 'WhatsApp', clique em 'Conectar Instância' e escaneie o QR Code com seu aparelho. Certifique-se de que o WhatsApp Web não esteja aberto em outro lugar (navegador ou outro computador) para evitar desconexões."
                            icon={<LifeBuoy className="text-emerald-500" size={18} />}
                        />
                        <FAQItem
                            question="Como a IA do ZapShop funciona?"
                            answer="Nossa IA utiliza o modelo GPT-4o-mini para atender seus clientes de forma humanizada. Você pode configurar a 'Personalidade' da IA na aba de configurações para que ela fale exatamente como você deseja, use gírias ou seja mais formal."
                            icon={<AlertCircle className="text-blue-500" size={18} />}
                        />
                        <FAQItem
                            question="Como configuro meus pagamentos?"
                            answer="Utilizamos o Mercado Pago para processar pagamentos de forma segura. Basta inserir seu 'Access Token' de produção nas configurações da loja. Seus clientes poderão pagar via PIX ou Cartão e o dinheiro cai direto na sua conta do MP."
                            icon={<Clock className="text-amber-500" size={18} />}
                        />
                        <FAQItem
                            question="Posso usar meu próprio domínio (ex: .com.br)?"
                            answer="Sim! Você pode configurar um domínio próprio apontando o DNS (Tipo A) para o IP 31.97.242.168. Após configurar o DNS, insira o domínio na aba 'Configurações' e o SSL será gerado automaticamente em até 24h."
                            icon={<CheckCircle2 className="text-purple-500" size={18} />}
                        />
                        <FAQItem
                            question="Qual o limite de produtos?"
                            answer="O limite depende do seu plano: Básico (100), Intermediário (300) ou PRO (Ilimitado). Se você atingir o limite, seus clientes ainda verão a vitrine, mas você não poderá cadastrar novos itens sem fazer o upgrade."
                            icon={<AlertCircle className="text-rose-500" size={18} />}
                        />
                        <FAQItem
                            question="Como gerenciar meus pedidos?"
                            answer="Sempre que um cliente finaliza uma compra, você recebe uma notificação no painel 'Pedidos'. Lá você pode ver os detalhes do cliente, produtos comprados e atualizar o status para 'Despachado' ou 'Entregue' para informar o cliente."
                            icon={<Clock className="text-indigo-500" size={18} />}
                        />
                    </div>

                    {/* Banner de Suporte no FAQ */}
                    <div className="bg-emerald-900 rounded-3xl p-8 text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:scale-110 transition-transform">
                            <BookOpen size={120} />
                        </div>
                        <div className="relative z-10 text-left">
                            <h4 className="text-xl font-black mb-2">Ainda precisa de ajuda?</h4>
                            <p className="text-emerald-100/80 text-sm mb-6 max-w-xs">Se sua dúvida não foi respondida acima, nossa equipe está pronta para te ajudar via WhatsApp.</p>
                            <a
                                href="https://wa.me/5521991000120?text=Preciso%20de%20ajuda%20com%20minha%20loja%20ZapShop"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-white text-emerald-900 font-black py-3 px-6 rounded-2xl hover:bg-emerald-50 transition-all active:scale-95 shadow-xl shadow-black/20"
                            >
                                <MessageSquare size={18} /> Chamar no WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={closeModal.isOpen}
                onClose={() => setCloseModal({ isOpen: false, ticketId: null, loading: false })}
                onConfirm={confirmCloseTicket}
                loading={closeModal.loading}
                title="Finalizar Chamado?"
                message="Deseja realmente finalizar este chamado? Esta ação indicará que seu problema foi resolvido."
                confirmText="Sim, Finalizar"
                cancelText="Voltar"
                type="warning"
            />
        </div>
    );
};

const FAQItem = ({ question, answer, icon }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="transition-all">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50/80 transition-all font-bold text-gray-900 gap-4"
            >
                <div className="flex items-center gap-4">
                    <div className="hidden sm:block">
                        {icon}
                    </div>
                    <span className="text-sm">{question}</span>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-emerald-500" /> : <ChevronDown size={18} className="text-gray-300" />}
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6 px-6' : 'max-h-0'}`}>
                <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-50/50 text-xs text-emerald-800 leading-relaxed">
                    {answer}
                </div>
            </div>
        </div>
    );
};

export default SupportPage;
